import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Car, MapPin, Navigation, Clock, X, ChevronRight, ChevronUp, ChevronDown,
  Loader2, Phone, Star, AlertCircle, DollarSign, Users, Zap, Search,
  Share2, Copy, Shield, AlertTriangle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { driverAPI, messagesAPI } from '../services/api';
import { useMessagingSocket } from '../hooks/useMessagingSocket';

// Leaflet CSS is loaded via CDN in index.html
const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTR = '&copy; OpenStreetMap contributors';

// Decode Google-encoded polyline (standard algorithm, no dependencies)
const decodePolyline = (encoded) => {
  if (!encoded) return [];
  const points = [];
  let index = 0, lat = 0, lng = 0;
  while (index < encoded.length) {
    let shift = 0, result = 0, byte;
    do { byte = encoded.charCodeAt(index++) - 63; result |= (byte & 0x1f) << shift; shift += 5; } while (byte >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);
    shift = 0; result = 0;
    do { byte = encoded.charCodeAt(index++) - 63; result |= (byte & 0x1f) << shift; shift += 5; } while (byte >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);
    points.push([lat / 1e5, lng / 1e5]);
  }
  return points;
};

// Vehicle type display info
const VEHICLE_OPTIONS = [
  { type: 'economy', icon: '🚗', label: 'Economy', desc: 'Affordable rides', capacity: '1-4' },
  { type: 'comfort', icon: '🚙', label: 'Comfort', desc: 'Extra legroom', capacity: '1-4' },
  { type: 'xl', icon: '🚐', label: 'XL', desc: 'Groups & luggage', capacity: '1-6' },
  { type: 'premium', icon: '🚘', label: 'Premium', desc: 'Luxury ride', capacity: '1-4' },
];

// Personal info filter — blocks phone numbers, emails, social handles
const filterPersonalInfo = (text) => {
  return text
    .replace(/(\+?\d[\d\s\-().]{7,}\d)/g, '[number hidden]')       // phone numbers
    .replace(/[\w.+-]+@[\w-]+\.[\w.]+/g, '[email hidden]')          // emails
    .replace(/@[\w]{2,}/g, '[handle hidden]')                        // social handles @username
    .replace(/\b\d{1,3}\s+[\w\s]+(?:road|street|lane|avenue|drive|close|way|place|court|crescent|terrace|grove|park)\b/gi, '[address hidden]'); // street addresses
};

const RideBooking = ({ booking, onNavigateToMessages }) => {
  const { getAuthToken, user } = useAuth();
  const { toast } = useToast();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const routeLineRef = useRef(null);
  const driverMarkersRef = useRef([]);
  const trackingIntervalRef = useRef(null);

  // Whether the user's geolocation was successfully detected. When false,
  // the booking UI surfaces a clear "set your pickup" prompt instead of
  // silently assuming a London default — important because Ariv rides are
  // currently London-only and a wrong fallback gives confusing fare quotes.
  const [geolocationDetected, setGeolocationDetected] = useState(false);

  // Booking flow state
  const [step, setStep] = useState('input'); // input, estimate, tracking
  const [pickupAddress, setPickupAddress] = useState('');
  const [dropoffAddress, setDropoffAddress] = useState('');
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState('economy');
  const [fareEstimates, setFareEstimates] = useState([]);
  const [surge, setSurge] = useState(false);
  const [surgeMultiplier, setSurgeMultiplier] = useState(1.0);
  const [passengers, setPassengers] = useState(1);
  const [notes, setNotes] = useState('');
  const [pickupTime, setPickupTime] = useState('ASAP');

  // Loading states
  const [geocoding, setGeocoding] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [requesting, setRequesting] = useState(false);

  // Active ride tracking
  const [activeRide, setActiveRide] = useState(null);
  const [trackingData, setTrackingData] = useState(null);

  // My rides list
  const [myRides, setMyRides] = useState([]);
  const [showMyRides, setShowMyRides] = useState(false);

  // Nearby drivers for map
  const [nearbyDrivers, setNearbyDrivers] = useState([]);

  // Route geometry from OSRM
  const [routeGeometry, setRouteGeometry] = useState(null);

  // Share ETA
  const [shareToken, setShareToken] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);

  // Lost item state (Uber-style workflow)
  const [lostItemRideId, setLostItemRideId] = useState(null);
  const [lostItemDesc, setLostItemDesc] = useState('');
  const [lostItemSubmitting, setLostItemSubmitting] = useState(false);
  const [showLostItemModal, setShowLostItemModal] = useState(false);
  const [lostItemStep, setLostItemStep] = useState('select'); // 'select' | 'describe' | 'contact' | 'success'
  const [lostItemCategory, setLostItemCategory] = useState('');
  const [lostItemRide, setLostItemRide] = useState(null);
  const [lostItemConvId, setLostItemConvId] = useState(null);

  // In-app ride chat (guest side)
  const [showRideChat, setShowRideChat] = useState(false);
  const [rideChatMessages, setRideChatMessages] = useState([]);
  const [rideChatInput, setRideChatInput] = useState('');
  const [rideChatConvId, setRideChatConvId] = useState(null);
  const [rideChatLoading, setRideChatLoading] = useState(false);
  const [rideChatTyping, setRideChatTyping] = useState(false);
  const [unreadChat, setUnreadChat] = useState(0);
  const chatEndRef = useRef(null);

  // Expanded ride (for review from history)
  const [expandedRideId, setExpandedRideId] = useState(null);
  const [historyReviewRating, setHistoryReviewRating] = useState(0);
  const [historyReviewHover, setHistoryReviewHover] = useState(0);
  const [historyReviewComment, setHistoryReviewComment] = useState('');
  const [historyReviewSubmitting, setHistoryReviewSubmitting] = useState(false);

  // Review state
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  // --- Cancellation Modal (Uber-style) ---
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // --- Location Permission Gate ---
  const [locationPermission, setLocationPermission] = useState(() => {
    return localStorage.getItem('rideLocationPermission') || null; // 'while_using' | 'once' | null
  });
  const [showLocationGate, setShowLocationGate] = useState(false);
  const [guestLiveLocation, setGuestLiveLocation] = useState(null);
  const guestWatchIdRef = useRef(null);

  // --- Post-Trip Review Popup ---
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [tripJustCompleted, setTripJustCompleted] = useState(false);
  const completedRideRef = useRef(null);

  // Live tracking refs for route line + pickup/dropoff markers
  const trackingRouteRef = useRef(null);
  const pickupMarkerRef = useRef(null);
  const dropoffMarkerRef = useRef(null);
  const lastTrackingRouteRef = useRef({ from: '', to: '' });

  // Redraw route polyline when OSRM geometry arrives
  useEffect(() => {
    if (routeGeometry && pickupCoords && dropoffCoords) {
      updateMapMarkers(pickupCoords, dropoffCoords);
    }
  }, [routeGeometry]);

  // --- Guest Live Location Tracking (for drop-off verification + auto-complete) ---
  useEffect(() => {
    if (!activeRide || !['in_progress', 'accepted', 'en_route', 'arrived_pickup'].includes(trackingData?.status || activeRide?.status)) {
      // Stop watching when no active ride
      if (guestWatchIdRef.current != null) {
        navigator.geolocation.clearWatch(guestWatchIdRef.current);
        guestWatchIdRef.current = null;
      }
      return;
    }

    // Start watching guest's location
    if (navigator.geolocation && guestWatchIdRef.current == null) {
      guestWatchIdRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          setGuestLiveLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 5000 }
      );
    }

    return () => {
      if (guestWatchIdRef.current != null) {
        navigator.geolocation.clearWatch(guestWatchIdRef.current);
        guestWatchIdRef.current = null;
      }
    };
  }, [activeRide?._id, trackingData?.status]);

  // --- Auto-detect trip completion (guest at drop-off) ---
  useEffect(() => {
    if (!guestLiveLocation || !trackingData || trackingData.status !== 'completed') return;
    // When backend marks trip completed, auto-transition guest to review
    if (!tripJustCompleted) {
      setTripJustCompleted(true);
      completedRideRef.current = { ...activeRide, ...trackingData };
      setShowReviewPopup(true);
    }
  }, [trackingData?.status, guestLiveLocation, tripJustCompleted]);

  // Also detect completion from polling even without live location
  useEffect(() => {
    if (trackingData?.status === 'completed' && !tripJustCompleted && activeRide) {
      setTripJustCompleted(true);
      completedRideRef.current = { ...activeRide, ...trackingData };
      setShowReviewPopup(true);
    }
  }, [trackingData?.status]);

  // Auto-fill pickup address from property booking (guest is at the property)
  useEffect(() => {
    if (booking?.propertyFullAddress) {
      // Check if guest has checked in (checkIn date <= today)
      const today = new Date().toISOString().slice(0, 10);
      const checkedIn = booking.checkIn && booking.checkIn <= today;
      if (checkedIn) {
        // After check-in, auto-fill pickup with property address
        if (!pickupAddress) {
          setPickupAddress(booking.propertyFullAddress);
        }
      } else {
        // Before check-in, auto-fill drop-off with property address (guest going to the property)
        if (!dropoffAddress) {
          setDropoffAddress(booking.propertyFullAddress);
        }
      }
    }
  }, [booking]);

  // --- Map Initialization (delayed until container is visible & sized) ---
  useEffect(() => {
    if (mapInstanceRef.current) return;
    let unmounted = false;
    let invalidateSizeTimer = null;

    const initMap = () => {
      if (unmounted) return false;
      const container = mapRef.current;
      if (!container || !document.contains(container) || container.offsetWidth === 0 || container.offsetHeight === 0) return false;

      const L = window.L;
      if (!L) return false;

      try {
        const map = L.map(container, {
          center: [51.5074, -0.1278], // London default
          zoom: 13,
          zoomControl: false
        });

        L.tileLayer(TILE_URL, { attribution: TILE_ATTR }).addTo(map);
        L.control.zoom({ position: 'bottomright' }).addTo(map);

        mapInstanceRef.current = map;

        // Ensure tiles render after container is fully painted
        invalidateSizeTimer = setTimeout(() => {
          if (!unmounted && mapInstanceRef.current) {
            try { map.invalidateSize(); } catch (e) {}
          }
        }, 200);

        // Try to get user location. Success -> mark detected and centre
        // the map. Failure -> leave the London-default centre but mark
        // geolocationDetected=false so the booking UI shows a clear
        // "we don't know where you are — please type your pickup" prompt
        // rather than letting users assume the app knows their location.
        navigator.geolocation?.getCurrentPosition(
          (pos) => {
            if (unmounted || !mapInstanceRef.current) return;
            try {
              const { latitude, longitude } = pos.coords;
              map.setView([latitude, longitude], 14);
              setGeolocationDetected(true);
              fetchNearbyDrivers(latitude, longitude);
            } catch (e) {
              // Silently ignore if map was removed
            }
          },
          () => {
            // Permission denied / unavailable. Leave the map at the
            // London centre but flag the state so the UI is honest.
            if (!unmounted) setGeolocationDetected(false);
          }
        );

        return true;
      } catch (e) {
        console.error('Map init error:', e);
        return false;
      }
    };

    // Retry until container is visible (tab may not be active yet)
    const retryTimer = setInterval(() => {
      if (unmounted) { clearInterval(retryTimer); return; }
      if (initMap()) clearInterval(retryTimer);
    }, 300);

    // Safety: stop retrying after 10 seconds
    const safetyTimeout = setTimeout(() => clearInterval(retryTimer), 10000);

    return () => {
      unmounted = true;
      clearInterval(retryTimer);
      clearTimeout(safetyTimeout);
      if (invalidateSizeTimer) clearTimeout(invalidateSizeTimer);
      if (trackingIntervalRef.current) clearInterval(trackingIntervalRef.current);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // --- Fetch nearby drivers for map display ---
  const fetchNearbyDrivers = async (lat, lng) => {
    try {
      const token = getAuthToken();
      const data = await driverAPI.getNearbyDrivers(lat, lng, token);
      setNearbyDrivers(data.drivers || []);
      updateDriverMarkers(data.drivers || []);
    } catch (e) {
      console.error('Failed to fetch nearby drivers:', e);
    }
  };

  const updateDriverMarkers = (drivers) => {
    const L = window.L;
    if (!L || !mapInstanceRef.current) return;

    // Remove old markers
    driverMarkersRef.current.forEach(m => m.remove());
    driverMarkersRef.current = [];

    drivers.forEach(d => {
      const icon = L.divIcon({
        className: 'driver-marker',
        html: `<div style="font-size:24px;transform:rotate(${d.heading || 0}deg)">🚗</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      });
      const marker = L.marker([d.lat, d.lng], { icon }).addTo(mapInstanceRef.current);
      driverMarkersRef.current.push(marker);
    });
  };

  // --- Geocoding ---
  const geocodeAddress = async (address, type) => {
    if (!address.trim()) return;
    setGeocoding(true);
    try {
      const token = getAuthToken();
      const result = await driverAPI.geocode(address, token);
      if (type === 'pickup') {
        setPickupCoords({ lat: result.lat, lng: result.lng });
        setPickupAddress(result.displayName || address);
      } else {
        setDropoffCoords({ lat: result.lat, lng: result.lng });
        setDropoffAddress(result.displayName || address);
      }
      const newPickup = type === 'pickup' ? { lat: result.lat, lng: result.lng } : pickupCoords;
      const newDropoff = type === 'dropoff' ? { lat: result.lat, lng: result.lng } : dropoffCoords;
      updateMapMarkers(newPickup || null, newDropoff || null);
    } catch (err) {
      toast({ title: 'Address not found', description: 'Please try a more specific address', variant: 'destructive' });
    }
    setGeocoding(false);
  };

  const updateMapMarkers = (pickup, dropoff) => {
    const L = window.L;
    if (!L || !mapInstanceRef.current) return;

    // Clear old markers & route
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    if (routeLineRef.current) {
      routeLineRef.current.remove();
      routeLineRef.current = null;
    }

    const bounds = [];

    if (pickup) {
      const pickupIcon = L.divIcon({
        className: 'custom-marker',
        html: '<div style="width:16px;height:16px;border-radius:50%;background:#22c55e;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>',
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      });
      const m = L.marker([pickup.lat, pickup.lng], { icon: pickupIcon }).addTo(mapInstanceRef.current);
      m.bindPopup('Pickup');
      markersRef.current.push(m);
      bounds.push([pickup.lat, pickup.lng]);
    }

    if (dropoff) {
      const dropoffIcon = L.divIcon({
        className: 'custom-marker',
        html: '<div style="width:16px;height:16px;border-radius:50%;background:#ef4444;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>',
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      });
      const m = L.marker([dropoff.lat, dropoff.lng], { icon: dropoffIcon }).addTo(mapInstanceRef.current);
      m.bindPopup('Drop-off');
      markersRef.current.push(m);
      bounds.push([dropoff.lat, dropoff.lng]);
    }

    if (pickup && dropoff) {
      // Draw OSRM route polyline if available, else dashed straight line
      const polyPoints = routeGeometry ? decodePolyline(routeGeometry) : null;
      if (polyPoints && polyPoints.length > 1) {
        routeLineRef.current = L.polyline(polyPoints, {
          color: '#1F3A68', weight: 5, opacity: 0.8
        }).addTo(mapInstanceRef.current);
      } else {
        routeLineRef.current = L.polyline(
          [[pickup.lat, pickup.lng], [dropoff.lat, dropoff.lng]],
          { color: '#1F3A68', weight: 4, dashArray: '8, 8', opacity: 0.7 }
        ).addTo(mapInstanceRef.current);
      }
    }

    if (bounds.length > 0) {
      mapInstanceRef.current.fitBounds(bounds, { padding: [60, 60], maxZoom: 15 });
    }
  };

  // --- Use My Location ---
  const useMyLocation = () => {
    navigator.geolocation?.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setPickupCoords({ lat: latitude, lng: longitude });
        try {
          const token = getAuthToken();
          const result = await driverAPI.reverseGeocode(latitude, longitude, token);
          setPickupAddress(result.address || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        } catch {
          setPickupAddress(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        }
        updateMapMarkers({ lat: latitude, lng: longitude }, dropoffCoords);
        fetchNearbyDrivers(latitude, longitude);
      },
      () => toast({ title: 'Location unavailable', description: 'Please enter your address manually', variant: 'destructive' })
    );
  };

  // --- Get Fare Estimates ---
  const getFareEstimates = async () => {
    if (!pickupCoords || !dropoffCoords) {
      toast({ title: 'Addresses required', description: 'Please enter both pickup and drop-off locations', variant: 'destructive' });
      return;
    }

    setEstimating(true);
    try {
      const token = getAuthToken();
      const data = await driverAPI.getFareEstimate(
        pickupCoords.lat, pickupCoords.lng,
        dropoffCoords.lat, dropoffCoords.lng,
        null, // Get all vehicle types
        token
      );
      setFareEstimates(data.estimates || []);
      setSurge(data.surge);
      setSurgeMultiplier(data.surgeMultiplier || 1.0);
      // Store route geometry from OSRM and redraw map with polyline
      if (data.route?.geometry) {
        setRouteGeometry(data.route.geometry);
      }
      setStep('estimate');
    } catch (err) {
      // Backend uses a structured 400 for out-of-area requests so we can
      // surface a friendly message instead of a generic "try again" toast.
      const detail = err?.response?.data?.detail;
      const code = (typeof detail === 'object' && detail?.code) || null;
      if (code === 'outside_service_area') {
        toast({
          title: 'Not available in your area yet',
          description: detail.message || "Ariv rides are launching in London first. We'll let you know when we're in your city.",
          variant: 'destructive',
        });
      } else {
        const msg = (typeof detail === 'object' ? detail.message : detail) || 'Could not estimate fare. Please try again.';
        toast({ title: 'Error', description: msg, variant: 'destructive' });
      }
    }
    setEstimating(false);
  };

  // --- Location Permission Handler ---
  const handleLocationPermission = (choice) => {
    // choice: 'while_using' or 'once'
    if (choice === 'while_using') {
      localStorage.setItem('rideLocationPermission', 'while_using');
      setLocationPermission('while_using');
    } else {
      // 'once' — don't persist, but allow this session
      setLocationPermission('once');
    }
    setShowLocationGate(false);
    // Now request the ride
    doRequestRide();
  };

  // --- Request Ride (with location gate) ---
  const requestRide = async () => {
    // Check location permission first
    if (!locationPermission || locationPermission === 'once') {
      // Need to check if we actually have geolocation access
      if (!navigator.geolocation) {
        toast({ title: 'Location Required', description: 'Your browser does not support location services. Cannot book a ride.', variant: 'destructive' });
        return;
      }
      // If no stored permission, show gate
      if (!locationPermission) {
        setShowLocationGate(true);
        return;
      }
      // 'once' — we had it this session, but verify again
      try {
        await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        });
      } catch {
        setShowLocationGate(true);
        return;
      }
    }
    doRequestRide();
  };

  const doRequestRide = async () => {
    setRequesting(true);
    try {
      const token = getAuthToken();
      const confirmedBooking = booking;
      const result = await driverAPI.requestRide({
        pickupAddress,
        dropoffAddress,
        pickupLat: pickupCoords?.lat,
        pickupLng: pickupCoords?.lng,
        dropoffLat: dropoffCoords?.lat,
        dropoffLng: dropoffCoords?.lng,
        pickupTime,
        vehicleType: selectedVehicle,
        passengers,
        bookingId: confirmedBooking?._id || null,
        notes
      }, token);

      setActiveRide(result);
      setStep('tracking');
      toast({ title: 'Ride Requested!', description: 'Looking for nearby drivers...' });

      // Start tracking
      startTracking(result._id);
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to request ride', variant: 'destructive' });
    }
    setRequesting(false);
  };

  // --- Real-Time Tracking ---
  const startTracking = (rideId) => {
    if (trackingIntervalRef.current) clearInterval(trackingIntervalRef.current);

    const fetchTracking = async () => {
      try {
        const token = getAuthToken();
        const data = await driverAPI.trackRide(rideId, token);
        setTrackingData(data);

        // Update driver marker on map
        if (data.driverLocation) {
          updateDriverOnMap(data);
        }

        // If ride completed or cancelled, stop tracking
        if (['completed', 'cancelled'].includes(data.status)) {
          clearInterval(trackingIntervalRef.current);
          trackingIntervalRef.current = null;
        }
      } catch (err) {
        console.error('Tracking error:', err);
      }
    };

    fetchTracking(); // Immediate first fetch
    trackingIntervalRef.current = setInterval(fetchTracking, 5000); // Poll every 5s
  };

  const updateDriverOnMap = (data) => {
    const L = window.L;
    if (!L || !mapInstanceRef.current) return;

    // Clear old driver markers
    driverMarkersRef.current.forEach(m => { try { m.remove(); } catch (_) {} });
    driverMarkersRef.current = [];

    if (data.driverLocation) {
      const rotation = data.driverLocation.heading || 0;
      const driverIcon = L.divIcon({
        className: 'driver-tracking-marker',
        html: `<div style="
          width:42px;height:42px;display:flex;align-items:center;justify-content:center;
          background:#1F3A68;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);
          transform:rotate(${rotation}deg);font-size:20px;
        ">🚕</div>`,
        iconSize: [42, 42],
        iconAnchor: [21, 21]
      });
      const marker = L.marker([data.driverLocation.lat, data.driverLocation.lng], {
        icon: driverIcon, zIndexOffset: 1000
      }).addTo(mapInstanceRef.current);
      marker.bindPopup(`Your driver${data.etaMinutes ? ` · ~${data.etaMinutes} min away` : ''}`);
      driverMarkersRef.current.push(marker);

      // --- Pickup & Drop-off Markers (add once) ---
      if (!pickupMarkerRef.current && data.pickup?.lat) {
        const pickupIcon = L.divIcon({
          className: 'pickup-marker',
          html: `<div style="background:#16a34a;color:white;padding:4px 8px;border-radius:8px;font-size:11px;font-weight:bold;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;gap:4px;border:2px solid white;">🟢 Pickup</div>`,
          iconSize: [90, 30], iconAnchor: [45, 30]
        });
        try {
          pickupMarkerRef.current = L.marker([data.pickup.lat, data.pickup.lng], { icon: pickupIcon })
            .addTo(mapInstanceRef.current);
          pickupMarkerRef.current.bindPopup(data.pickup.address || 'Pickup');
        } catch (_) {}
      }
      if (!dropoffMarkerRef.current && data.dropoff?.lat) {
        const dropoffIcon = L.divIcon({
          className: 'dropoff-marker',
          html: `<div style="background:#dc2626;color:white;padding:4px 8px;border-radius:8px;font-size:11px;font-weight:bold;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;gap:4px;border:2px solid white;">🔴 Drop-off</div>`,
          iconSize: [100, 30], iconAnchor: [50, 30]
        });
        try {
          dropoffMarkerRef.current = L.marker([data.dropoff.lat, data.dropoff.lng], { icon: dropoffIcon })
            .addTo(mapInstanceRef.current);
          dropoffMarkerRef.current.bindPopup(data.dropoff.address || 'Drop-off');
        } catch (_) {}
      }

      // --- OSRM Live Route: driver → destination (updates as driver moves) ---
      let destLat, destLng;
      if (['accepted', 'en_route'].includes(data.status) && data.pickup?.lat) {
        destLat = data.pickup.lat;
        destLng = data.pickup.lng;
      } else if (['arrived_pickup', 'in_progress'].includes(data.status) && data.dropoff?.lat) {
        destLat = data.dropoff.lat;
        destLng = data.dropoff.lng;
      }

      if (destLat && destLng) {
        const fromKey = `${data.driverLocation.lat.toFixed(4)},${data.driverLocation.lng.toFixed(4)}`;
        const toKey = `${destLat.toFixed(4)},${destLng.toFixed(4)}`;
        if (lastTrackingRouteRef.current.from !== fromKey || lastTrackingRouteRef.current.to !== toKey) {
          lastTrackingRouteRef.current = { from: fromKey, to: toKey };
          // Fetch route from OSRM
          fetch(`https://router.project-osrm.org/route/v1/driving/${data.driverLocation.lng},${data.driverLocation.lat};${destLng},${destLat}?overview=full&geometries=polyline`)
            .then(r => r.json())
            .then(routeData => {
              if (!routeData.routes?.[0] || !mapInstanceRef.current) return;
              const points = decodePolyline(routeData.routes[0].geometry);
              if (points.length < 2) return;
              // Clear old route
              if (trackingRouteRef.current) { try { trackingRouteRef.current.remove(); } catch (_) {} }
              trackingRouteRef.current = L.polyline(points, {
                color: '#1F3A68', weight: 5, opacity: 0.7
              }).addTo(mapInstanceRef.current);
            })
            .catch(() => {
              // Fallback: straight line
              if (!mapInstanceRef.current) return;
              if (trackingRouteRef.current) { try { trackingRouteRef.current.remove(); } catch (_) {} }
              try {
                trackingRouteRef.current = L.polyline(
                  [[data.driverLocation.lat, data.driverLocation.lng], [destLat, destLng]],
                  { color: '#1F3A68', weight: 3, opacity: 0.5, dashArray: '10, 10' }
                ).addTo(mapInstanceRef.current);
              } catch (_) {}
            });
        }
      }

      // Fit map to show driver + pickup + dropoff
      const points = [[data.driverLocation.lat, data.driverLocation.lng]];
      if (data.pickup?.lat) points.push([data.pickup.lat, data.pickup.lng]);
      if (data.dropoff?.lat) points.push([data.dropoff.lat, data.dropoff.lng]);
      if (points.length > 1) {
        try { mapInstanceRef.current.fitBounds(points, { padding: [50, 50], maxZoom: 16 }); } catch (_) {}
      }
    }
  };

  // --- Fetch My Rides ---
  const fetchMyRides = async () => {
    try {
      const token = getAuthToken();
      const data = await driverAPI.getMyRideRequests(token);
      setMyRides(data);

      // Check for any active ride
      const active = data.find(r => ['requested', 'accepted', 'en_route', 'arrived_pickup', 'in_progress'].includes(r.status));
      if (active) {
        setActiveRide(active);
        setStep('tracking');
        startTracking(active._id);
      }
    } catch (err) {
      console.error('Failed to fetch rides:', err);
    }
  };

  // Lost item categories (Uber-style)
  const LOST_ITEM_CATEGORIES = [
    { id: 'phone', icon: '📱', label: 'Phone or tablet' },
    { id: 'wallet', icon: '👛', label: 'Wallet, purse or bag' },
    { id: 'keys', icon: '🔑', label: 'Keys' },
    { id: 'clothing', icon: '🧥', label: 'Clothing or umbrella' },
    { id: 'electronics', icon: '🎧', label: 'Electronics (headphones, charger, etc.)' },
    { id: 'documents', icon: '📄', label: 'Documents or ID' },
    { id: 'other', icon: '📦', label: 'Other item' },
  ];

  const openLostItemModal = (ride) => {
    setLostItemRide(ride);
    setLostItemRideId(ride._id);
    setLostItemStep('select');
    setLostItemCategory('');
    setLostItemDesc('');
    setLostItemConvId(null);
    setShowLostItemModal(true);
  };

  const handleReportLostItem = async () => {
    if (!lostItemDesc.trim() || !lostItemRideId) return;
    setLostItemSubmitting(true);
    try {
      const token = getAuthToken();
      const categoryLabel = LOST_ITEM_CATEGORIES.find(c => c.id === lostItemCategory)?.label || '';
      const fullDesc = categoryLabel ? `${categoryLabel}: ${lostItemDesc.trim()}` : lostItemDesc.trim();
      const result = await driverAPI.reportLostItem(lostItemRideId, fullDesc, token);
      setLostItemConvId(result.conversationId);
      setLostItemStep('success');
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to report lost item', variant: 'destructive' });
    }
    setLostItemSubmitting(false);
  };

  // --- WebSocket for ride chat (guest side) ---
  const handleNewMessage = useCallback((conversationId, message) => {
    if (conversationId === rideChatConvId && message.senderId !== user?._id) {
      setRideChatMessages(prev => [...prev, message]);
      if (!showRideChat) setUnreadChat(prev => prev + 1);
    }
  }, [rideChatConvId, showRideChat, user]);

  const handleTypingIndicator = useCallback((conversationId, senderId, senderName, isTyping) => {
    if (conversationId === rideChatConvId && senderId !== user?._id) {
      setRideChatTyping(isTyping);
    }
  }, [rideChatConvId, user]);

  const { sendTypingIndicator } = useMessagingSocket({
    onNewMessage: handleNewMessage,
    onTyping: handleTypingIndicator,
    activeConversationId: showRideChat ? rideChatConvId : null
  });

  // Load chat when active ride has a conversation
  // Also polls for conversationId if ride is active but conv not yet created
  useEffect(() => {
    const rideStatus = activeRide?.status;
    const isActive = ['accepted', 'en_route', 'arrived_pickup', 'in_progress'].includes(rideStatus);

    if (!activeRide || !isActive) {
      setRideChatConvId(null);
      setRideChatMessages([]);
      setShowRideChat(false);
      setUnreadChat(0);
      return;
    }

    const loadChat = async () => {
      let convId = activeRide.conversationId;
      // If no conversationId yet, re-fetch the ride to get it
      if (!convId) {
        try {
          const token = getAuthToken();
          const freshRide = await driverAPI.getRideRequest(activeRide._id, token);
          convId = freshRide?.conversationId;
          if (convId) setActiveRide(prev => prev ? { ...prev, conversationId: convId } : prev);
        } catch (e) { /* ride fetch failed */ }
      }
      if (!convId) return;
      try {
        const token = getAuthToken();
        const data = await messagesAPI.getConversation(convId, token);
        setRideChatConvId(convId);
        setRideChatMessages(data.messages || []);
      } catch (e) { console.error('Failed to load ride chat:', e); }
    };
    loadChat();
  }, [activeRide?._id, activeRide?.conversationId, activeRide?.status, getAuthToken]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [rideChatMessages]);

  const sendRideChat = async (content) => {
    if (!content.trim() || !rideChatConvId) return;
    // Filter personal info before sending
    const filtered = filterPersonalInfo(content.trim());
    if (filtered !== content.trim()) {
      toast({ title: 'Personal info removed', description: 'Phone numbers, emails, and addresses are not shared for safety.', variant: 'destructive' });
    }
    setRideChatLoading(true);
    setRideChatInput('');
    try {
      const token = getAuthToken();
      // Optimistically add message to local state so guest sees it immediately
      const optimisticMsg = {
        _id: 'temp-' + Date.now(),
        conversationId: rideChatConvId,
        senderId: user?._id,
        senderName: user?.name?.split(' ')[0] || 'You',
        content: filtered,
        createdAt: new Date().toISOString()
      };
      setRideChatMessages(prev => [...prev, optimisticMsg]);
      await messagesAPI.sendMessage({ conversationId: rideChatConvId, content: filtered }, token);
    } catch (e) {
      toast({ title: 'Failed to send', variant: 'destructive' });
    }
    setRideChatLoading(false);
  };

  // Submit review from ride history
  const submitHistoryReview = async (rideId) => {
    if (!historyReviewRating || !rideId) return;
    setHistoryReviewSubmitting(true);
    try {
      const token = getAuthToken();
      await driverAPI.rateRide(rideId, historyReviewRating, historyReviewComment, token);
      toast({ title: 'Review submitted!', description: `You rated your driver ${historyReviewRating} stars` });
      // Update the ride in myRides list
      setMyRides(prev => prev.map(r => r._id === rideId ? { ...r, rated: true, rating: historyReviewRating } : r));
      setExpandedRideId(null);
      setHistoryReviewRating(0);
      setHistoryReviewComment('');
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Could not submit review', variant: 'destructive' });
    }
    setHistoryReviewSubmitting(false);
  };

  useEffect(() => {
    fetchMyRides();
    return () => {
      if (trackingIntervalRef.current) clearInterval(trackingIntervalRef.current);
    };
  }, []);

  // --- Uber-style Cancellation Reasons ---
  const GUEST_CANCEL_REASONS = [
    { id: 'taking_too_long', label: 'Driver is taking too long', icon: '⏱️' },
    { id: 'wrong_address', label: 'I entered the wrong address', icon: '📍' },
    { id: 'changed_plans', label: 'I changed my plans', icon: '🔄' },
    { id: 'found_other_ride', label: 'I found another ride', icon: '🚗' },
    { id: 'driver_asked', label: 'Driver asked me to cancel', icon: '💬' },
    { id: 'eta_too_long', label: 'ETA is too long', icon: '🕐' },
    { id: 'safety_concern', label: 'I have a safety concern', icon: '⚠️' },
    { id: 'other', label: 'Other reason', icon: '📝' },
  ];

  // --- Cancel Ride (with reason modal) ---
  const cancelRide = async () => {
    if (!activeRide?._id || !cancelReason) return;
    setCancelling(true);
    try {
      const token = getAuthToken();
      const reasonLabel = GUEST_CANCEL_REASONS.find(r => r.id === cancelReason)?.label || cancelReason;
      await driverAPI.cancelRideRequest(activeRide._id, token, reasonLabel);
      toast({ title: 'Ride Cancelled', description: 'Your ride has been cancelled.' });
      if (trackingIntervalRef.current) clearInterval(trackingIntervalRef.current);
      setActiveRide(null);
      setTrackingData(null);
      setStep('input');
      setShowCancelModal(false);
      setCancelReason('');
      fetchMyRides();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Cannot cancel ride', variant: 'destructive' });
    } finally {
      setCancelling(false);
    }
  };

  // --- Submit taxi review ---
  const submitTaxiReview = async () => {
    if (!reviewRating || !activeRide?._id) return;
    setReviewSubmitting(true);
    try {
      const token = getAuthToken();
      await driverAPI.rateRide(activeRide._id, reviewRating, reviewComment, token);
      setReviewSubmitted(true);
      setActiveRide(prev => prev ? { ...prev, rated: true, rating: reviewRating } : prev);
      toast({ title: 'Review submitted!', description: `You rated your driver ${reviewRating} stars` });
    } catch (e) {
      console.error('Failed to submit review:', e);
      toast({
        title: 'Error',
        description: e.response?.data?.detail || 'Could not submit review',
        variant: 'destructive'
      });
    } finally {
      setReviewSubmitting(false);
    }
  };

  // --- Share ETA ---
  const handleShareETA = async () => {
    if (!activeRide?._id) return;
    try {
      const token = getAuthToken();
      let token_ = shareToken;
      if (!token_) {
        const res = await driverAPI.createShareLink(activeRide._id, token);
        token_ = res.shareToken;
        setShareToken(token_);
      }
      const url = `${window.location.origin}/track/${token_}`;
      if (navigator.share) {
        await navigator.share({ title: 'Track My Ride', text: 'Follow my live ride ETA', url });
      } else {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 3000);
        toast({ title: 'Link copied!', description: 'Share this link so others can track your ride' });
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast({ title: 'Error', description: 'Could not generate share link', variant: 'destructive' });
      }
    }
  };

  // --- Status helpers ---
  const getStatusInfo = (status) => {
    const map = {
      requested: { label: 'Finding driver...', color: 'bg-amber-100 text-amber-800', icon: <Loader2 className="animate-spin" size={16} /> },
      accepted: { label: 'Driver accepted', color: 'bg-blue-100 text-blue-800', icon: <Car size={16} /> },
      en_route: { label: 'Driver en route', color: 'bg-purple-100 text-purple-800', icon: <Navigation size={16} /> },
      arrived_pickup: { label: 'Driver arrived', color: 'bg-green-100 text-green-800', icon: <MapPin size={16} /> },
      in_progress: { label: 'Trip in progress', color: 'bg-indigo-100 text-indigo-800', icon: <Navigation size={16} /> },
      completed: { label: 'Trip completed', color: 'bg-green-100 text-green-800', icon: <Star size={16} /> },
      cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-800', icon: <X size={16} /> },
    };
    return map[status] || { label: status, color: 'bg-gray-100', icon: null };
  };

  const selectedEstimate = fareEstimates.find(e => e.vehicleType === selectedVehicle);

  return (
    <div className="relative" data-testid="ride-booking">
      {/* Map Container */}
      <div
        ref={mapRef}
        className="w-full h-[400px] md:h-[500px] rounded-xl overflow-hidden shadow-lg border border-gray-200"
        data-testid="ride-map"
      />

      {/* Overlay Panel */}
      <div className="mt-4 space-y-4">

        {/* === STEP 1: INPUT === */}
        {step === 'input' && (
          <Card className="shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Car size={20} className="text-[#1F3A68]" />
                Book a Ride
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Booking address hint */}
              {booking?.propertyFullAddress && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-blue-700">
                    📍 {booking.checkIn && booking.checkIn <= new Date().toISOString().slice(0, 10)
                      ? `Pickup auto-filled from your stay at ${booking.propertyTitle || 'your property'}`
                      : `Drop-off auto-filled to ${booking.propertyTitle || 'your property'}`
                    }
                  </p>
                  <p className="text-sm text-blue-900 mt-0.5">{booking.propertyFullAddress}</p>
                  <p className="text-xs text-blue-500 mt-1">You can change either address below if you prefer.</p>
                </div>
              )}

              {/* Geolocation honesty banner — only when we couldn't detect
                  the user's location, AND they haven't already typed a
                  pickup. Tells them the map default isn't them, so the
                  fare estimate is meaningful only after they type a real
                  pickup. */}
              {!geolocationDetected && !pickupCoords && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                  We couldn't detect your location. Please type your pickup address below so we can quote the right fare. Ariv rides currently operate in London only.
                </div>
              )}

              {/* Pickup */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Pickup</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-green-500" />
                    <Input
                      value={pickupAddress}
                      onChange={e => {
                        setPickupAddress(e.target.value);
                        if (pickupCoords) setPickupCoords(null);
                      }}
                      onBlur={() => pickupAddress && !pickupCoords && geocodeAddress(pickupAddress, 'pickup')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && pickupAddress) {
                          e.preventDefault();
                          geocodeAddress(pickupAddress, 'pickup');
                        }
                      }}
                      placeholder="Enter pickup address"
                      className="pl-8"
                      data-testid="ride-pickup-input"
                    />
                  </div>
                  <Button variant="outline" size="icon" onClick={useMyLocation} title="Use my location">
                    <Navigation size={16} />
                  </Button>
                  <Button
                    variant="outline" size="icon"
                    onClick={() => geocodeAddress(pickupAddress, 'pickup')}
                    disabled={geocoding || !pickupAddress}
                  >
                    <Search size={16} />
                  </Button>
                </div>
                {pickupCoords && (
                  <p className="text-xs text-green-600 mt-1">✓ Location set</p>
                )}
              </div>

              {/* Dropoff */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Drop-off</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-red-500" />
                    <Input
                      value={dropoffAddress}
                      onChange={e => {
                        setDropoffAddress(e.target.value);
                        // Clear old coords when address changes so re-geocoding can happen
                        if (dropoffCoords) setDropoffCoords(null);
                      }}
                      onBlur={() => dropoffAddress && !dropoffCoords && geocodeAddress(dropoffAddress, 'dropoff')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && dropoffAddress) {
                          e.preventDefault();
                          geocodeAddress(dropoffAddress, 'dropoff');
                        }
                      }}
                      placeholder="Address, postcode, or landmark"
                      className="pl-8"
                      data-testid="ride-dropoff-input"
                    />
                  </div>
                  <Button
                    variant="outline" size="icon"
                    onClick={() => geocodeAddress(dropoffAddress, 'dropoff')}
                    disabled={geocoding || !dropoffAddress}
                  >
                    <Search size={16} />
                  </Button>
                </div>
                {dropoffCoords && (
                  <p className="text-xs text-green-600 mt-1">✓ Location set</p>
                )}
                {!dropoffCoords && dropoffAddress && !geocoding && (
                  <p className="text-xs text-amber-600 mt-1">Press Enter or click 🔍 to locate</p>
                )}
              </div>

              {/* Pickup Time */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">When</label>
                  <select
                    value={pickupTime}
                    onChange={e => setPickupTime(e.target.value)}
                    className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm"
                  >
                    <option value="ASAP">Now</option>
                    <option value="15min">In 15 minutes</option>
                    <option value="30min">In 30 minutes</option>
                    <option value="1hr">In 1 hour</option>
                  </select>
                </div>
                <div className="w-24">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Riders</label>
                  <select
                    value={passengers}
                    onChange={e => setPassengers(parseInt(e.target.value))}
                    className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm"
                  >
                    {[1, 2, 3, 4, 5, 6].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Get Fare Button */}
              <Button
                className="w-full bg-[#1F3A68] hover:bg-[#1F3A68]/90 h-12 text-base"
                onClick={getFareEstimates}
                disabled={estimating || !pickupCoords || !dropoffCoords}
                data-testid="get-fare-btn"
              >
                {estimating ? (
                  <><Loader2 size={18} className="mr-2 animate-spin" /> Estimating...</>
                ) : (
                  <><DollarSign size={18} className="mr-2" /> See Prices</>
                )}
              </Button>

              {geocoding && (
                <p className="text-sm text-gray-500 text-center flex items-center justify-center gap-1">
                  <Loader2 size={14} className="animate-spin" /> Finding location...
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* === STEP 2: FARE ESTIMATES === */}
        {step === 'estimate' && fareEstimates.length > 0 && (
          <Card className="shadow-lg">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Choose Your Ride</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setStep('input')}>
                  <ChevronUp size={16} className="mr-1" /> Edit
                </Button>
              </div>
              {surge && (
                <div className="flex items-center gap-2 bg-amber-50 p-2 rounded-lg mt-2">
                  <Zap size={16} className="text-amber-600" />
                  <span className="text-sm text-amber-700 font-medium">
                    Surge pricing active ({surgeMultiplier}x) — high demand in your area
                  </span>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Vehicle Options */}
              {VEHICLE_OPTIONS.map(v => {
                const est = fareEstimates.find(e => e.vehicleType === v.type);
                if (!est) return null;
                const isSelected = selectedVehicle === v.type;

                return (
                  <div
                    key={v.type}
                    className={`flex items-center gap-4 p-3 rounded-xl cursor-pointer transition-all border-2 ${
                      isSelected
                        ? 'border-[#1F3A68] bg-blue-50/50'
                        : 'border-transparent bg-gray-50 hover:bg-gray-100'
                    }`}
                    onClick={() => setSelectedVehicle(v.type)}
                    data-testid={`vehicle-${v.type}`}
                  >
                    <span className="text-3xl">{v.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold">{v.label}</h4>
                        <span className="text-xs text-gray-500">
                          <Users size={12} className="inline mr-1" />{v.capacity}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">{v.desc} · {est.distance} mi · ~{est.duration} min</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">£{est.total.toFixed(2)}</p>
                      {surge && <p className="text-xs text-amber-600">{surgeMultiplier}x</p>}
                    </div>
                  </div>
                );
              })}

              {/* Notes */}
              <div>
                <Input
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Add a note for your driver (optional)"
                  className="text-sm"
                />
              </div>

              {/* Fare Breakdown */}
              {selectedEstimate && (
                <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-gray-600">Base fare</span><span>£{selectedEstimate.baseFare.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Distance ({selectedEstimate.distance} mi)</span><span>£{selectedEstimate.distanceCharge.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Time (~{selectedEstimate.duration} min)</span><span>£{selectedEstimate.timeCharge.toFixed(2)}</span></div>
                  {surge && (
                    <div className="flex justify-between text-amber-700"><span>Surge ({surgeMultiplier}x)</span><span>applied</span></div>
                  )}
                  {selectedEstimate.cazSurcharge > 0 && (
                    <div className="flex justify-between text-amber-700" title={selectedEstimate.cazDescription || 'UK Clean Air Zone surcharge'}>
                      <span>Clean Air Zone</span>
                      <span>+£{selectedEstimate.cazSurcharge.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between"><span className="text-gray-600">Service fee</span><span>£{(selectedEstimate.serviceFee || selectedEstimate.platformFee).toFixed(2)}</span></div>
                  <div className="flex justify-between font-bold border-t pt-1 mt-1"><span>Total</span><span>£{selectedEstimate.total.toFixed(2)}</span></div>
                </div>
              )}

              {/* Confirm Button */}
              <Button
                className="w-full bg-[#1F3A68] hover:bg-[#1F3A68]/90 h-12 text-base"
                onClick={requestRide}
                disabled={requesting}
                data-testid="confirm-ride-btn"
              >
                {requesting ? (
                  <><Loader2 size={18} className="mr-2 animate-spin" /> Requesting...</>
                ) : (
                  <>Confirm {VEHICLE_OPTIONS.find(v => v.type === selectedVehicle)?.label} · £{selectedEstimate?.total.toFixed(2)}</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* === STEP 3: LIVE TRACKING === */}
        {step === 'tracking' && activeRide && (
          <Card className="shadow-lg">
            <CardContent className="p-4 space-y-4">
              {/* Status Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {(() => {
                    const status = trackingData?.status || activeRide?.status;
                    const info = getStatusInfo(status);
                    return (
                      <>
                        <div className={`p-2 rounded-full ${info.color}`}>{info.icon}</div>
                        <div>
                          <p className="font-bold text-lg">{info.label}</p>
                          <p className="text-xs text-gray-500">#{activeRide.rideNumber}</p>
                        </div>
                      </>
                    );
                  })()}
                </div>
                {trackingData?.etaMinutes && !['completed', 'cancelled'].includes(trackingData.status) && (
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#1F3A68]">{trackingData.etaMinutes}</p>
                    <p className="text-xs text-gray-500">min ETA</p>
                  </div>
                )}
              </div>

              {/* Driver Info */}
              {(trackingData?.driverName || activeRide.driverName) && (
                <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-[#1F3A68] text-white flex items-center justify-center text-xl font-bold">
                    {(trackingData?.driverName || activeRide.driverName || 'D')[0]}
                  </div>
                  <div className="flex-1">
                    <p className="font-bold">{trackingData?.driverName || activeRide.driverName}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>{trackingData?.vehicleType || activeRide.vehicleType}</span>
                      {(trackingData?.vehiclePlate || activeRide.vehiclePlate) && (
                        <Badge variant="secondary">{trackingData?.vehiclePlate || activeRide.vehiclePlate}</Badge>
                      )}
                    </div>
                  </div>
                  {(trackingData?.driverPhone || activeRide.driverPhone) && (
                    <a
                      href={`tel:${trackingData?.driverPhone || activeRide.driverPhone}`}
                      className="p-2 rounded-full bg-green-100 text-green-700 hover:bg-green-200"
                    >
                      <Phone size={20} />
                    </a>
                  )}
                </div>
              )}

              {/* Message Driver Button + Chat Panel */}
              {rideChatConvId && !['completed', 'cancelled'].includes(trackingData?.status || activeRide.status) && (
                <div>
                  <Button
                    variant="outline"
                    className={`w-full border-blue-200 text-blue-700 hover:bg-blue-50 ${showRideChat ? 'bg-blue-50' : ''}`}
                    onClick={() => { setShowRideChat(!showRideChat); setUnreadChat(0); }}
                  >
                    💬 {showRideChat ? 'Hide Chat' : 'Message Driver'}
                    {unreadChat > 0 && <Badge className="ml-2 bg-red-500 text-white text-xs">{unreadChat}</Badge>}
                  </Button>

                  {showRideChat && (
                    <div className="mt-2 border rounded-xl overflow-hidden">
                      {/* Messages */}
                      <div className="h-48 overflow-y-auto p-3 space-y-2 bg-gray-50">
                        {rideChatMessages.length === 0 && (
                          <p className="text-center text-gray-400 text-sm py-6">Send a message to coordinate with your driver</p>
                        )}
                        {rideChatMessages.map((msg, i) => {
                          const isMe = msg.senderId === user?._id;
                          return (
                            <div key={msg._id || i} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                              <div className={`max-w-[75%] px-3 py-1.5 rounded-2xl text-sm ${isMe ? 'bg-[#1F3A68] text-white rounded-br-sm' : 'bg-white text-gray-800 border rounded-bl-sm'}`}>
                                <p>{msg.content}</p>
                                <p className={`text-xs mt-0.5 ${isMe ? 'text-blue-200' : 'text-gray-400'}`}>
                                  {msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                        {rideChatTyping && (
                          <div className="flex justify-start"><div className="bg-gray-200 text-gray-600 px-3 py-1.5 rounded-2xl text-sm italic">typing...</div></div>
                        )}
                        <div ref={chatEndRef} />
                      </div>
                      {/* Quick replies */}
                      <div className="px-3 py-2 border-t bg-white flex gap-1.5 overflow-x-auto no-scrollbar">
                        {["I'm outside", "On my way down", "2 minutes", "Where are you?"].map((qr, i) => (
                          <button key={i} onClick={() => sendRideChat(qr)} className="flex-shrink-0 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium hover:bg-blue-100 border border-blue-200">{qr}</button>
                        ))}
                      </div>
                      {/* Text input */}
                      <div className="px-3 py-2 border-t flex gap-2">
                        <input
                          type="text" value={rideChatInput}
                          onChange={(e) => { setRideChatInput(e.target.value); if (rideChatConvId) sendTypingIndicator(rideChatConvId, true); }}
                          onKeyDown={(e) => e.key === 'Enter' && sendRideChat(rideChatInput)}
                          placeholder="Type a message..."
                          className="flex-1 text-sm border rounded-full px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                        <button onClick={() => sendRideChat(rideChatInput)} disabled={!rideChatInput.trim() || rideChatLoading}
                          className="px-3 py-1.5 bg-[#1F3A68] text-white rounded-full text-sm font-medium disabled:opacity-50 hover:bg-[#2a4d82]">
                          Send
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 text-center pb-2">Phone numbers, emails & addresses are automatically hidden for your safety</p>
                    </div>
                  )}
                </div>
              )}

              {/* Route Summary */}
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <div className="w-3 h-3 mt-1.5 rounded-full bg-green-500 flex-shrink-0" />
                  <p className="text-sm">{trackingData?.pickup?.address || activeRide.pickupAddress}</p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-3 h-3 mt-1.5 rounded-full bg-red-500 flex-shrink-0" />
                  <p className="text-sm">{trackingData?.dropoff?.address || activeRide.dropoffAddress}</p>
                </div>
                {/* Google Maps link for guest */}
                {(trackingData?.pickup?.lat || activeRide.pickupLat) && (trackingData?.dropoff?.lat || activeRide.dropoffLat) && (
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&origin=${trackingData?.pickup?.lat || activeRide.pickupLat},${trackingData?.pickup?.lng || activeRide.pickupLng}&destination=${trackingData?.dropoff?.lat || activeRide.dropoffLat},${trackingData?.dropoff?.lng || activeRide.dropoffLng}&travelmode=driving`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-green-50 border border-green-200 rounded-lg py-2 text-sm font-medium text-green-700 hover:bg-green-100 transition-colors"
                  >
                    🧭 View Route in Google Maps
                  </a>
                )}
              </div>

              {/* Traffic Badge */}
              {trackingData?.inTraffic && ['in_progress', 'en_route'].includes(trackingData?.status) && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 p-2.5 rounded-lg">
                  <AlertTriangle size={16} className="text-amber-600" />
                  <span className="text-sm font-medium text-amber-700">Your driver is in slow traffic</span>
                </div>
              )}

              {/* Location verification notice — shown to guest when trip is in progress */}
              {trackingData?.status === 'in_progress' && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Shield size={16} className="text-indigo-600" />
                    <span className="font-semibold text-indigo-900 text-sm">Location Verified Trip</span>
                  </div>
                  <p className="text-xs text-indigo-600">
                    Your trip will automatically complete when you arrive at your destination.
                    {guestLiveLocation ? ' 📍 Location active' : ' Enable location for auto-completion.'}
                  </p>
                </div>
              )}

              {/* Fare */}
              {(trackingData?.fare || activeRide.fareEstimate) && (
                <div className="flex justify-between items-center bg-blue-50 p-3 rounded-lg">
                  <span className="text-sm font-medium text-[#1F3A68]">Estimated Fare</span>
                  <span className="text-lg font-bold text-[#1F3A68]">
                    £{((trackingData?.fare?.total || activeRide.fareEstimate?.total || activeRide.estimatedFare || 0)).toFixed(2)}
                  </span>
                </div>
              )}

              {/* Pay-for-ride CTA — appears after the ride completes and
                  before payment is settled. We removed the cash-on-delivery
                  default for the Ride vertical: guests pay through Stripe
                  Checkout, the platform takes its commission, and the
                  driver's share is transferred to their Connect account. */}
              {(trackingData?.status || activeRide.status) === 'completed'
                && (trackingData?.paymentStatus || activeRide.paymentStatus) !== 'paid' && (
                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-900 font-semibold mb-1">Pay for your ride</p>
                  <p className="text-blue-700 text-sm mb-2">
                    Trip complete. Settle up to release the driver's share.
                  </p>
                  <Button
                    className="bg-[#CB3B3B] hover:bg-[#CB3B3B]/90"
                    onClick={async () => {
                      try {
                        const token = getAuthToken();
                        const { paymentsAPI } = await import('../services/api');
                        const { checkoutUrl } = await paymentsAPI.createRideCheckout(
                          activeRide._id,
                          window.location.origin,
                          token,
                        );
                        if (checkoutUrl) {
                          window.location.href = checkoutUrl;
                        } else {
                          toast({ title: 'Error', description: 'Could not start payment', variant: 'destructive' });
                        }
                      } catch (err) {
                        toast({
                          title: 'Error',
                          description: err.response?.data?.detail || 'Could not start payment',
                          variant: 'destructive',
                        });
                      }
                    }}
                  >
                    Pay £{((trackingData?.fare?.total || activeRide.fareEstimate?.total || activeRide.estimatedFare || 0)).toFixed(2)}
                  </Button>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                {/* Share ETA button — visible during active ride */}
                {!['completed', 'cancelled'].includes(trackingData?.status || activeRide.status) && (
                  <Button
                    variant="outline"
                    className="border-[#1F3A68] text-[#1F3A68] hover:bg-blue-50"
                    onClick={handleShareETA}
                  >
                    {shareCopied ? <><Copy size={16} className="mr-1" /> Copied!</> : <><Share2 size={16} className="mr-1" /> Share ETA</>}
                  </Button>
                )}
                {['requested', 'accepted', 'en_route'].includes(trackingData?.status || activeRide.status) && (() => {
                  const driverNearby = trackingData?.distanceToPickupMeters != null && trackingData.distanceToPickupMeters <= 500;
                  const canCancel = !driverNearby;
                  return canCancel ? (
                    <Button
                      variant="outline"
                      className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => setShowCancelModal(true)}
                    >
                      <X size={16} className="mr-1" /> Cancel Ride
                    </Button>
                  ) : (
                    <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      <span className="text-gray-400 text-sm">🚫</span>
                      <span className="text-xs text-gray-500">
                        Driver is {trackingData.distanceToPickupMeters}m away — cancel unavailable within 500m
                      </span>
                    </div>
                  );
                })()}
                {(trackingData?.status || activeRide.status) === 'cancelled' && (
                  <Button
                    className="flex-1 bg-[#1F3A68]"
                    onClick={() => {
                      setActiveRide(null);
                      setTrackingData(null);
                      setStep('input');
                      setPickupAddress('');
                      setDropoffAddress('');
                      setPickupCoords(null);
                      setDropoffCoords(null);
                      setFareEstimates([]);
                      setReviewRating(0);
                      setReviewHover(0);
                      setReviewComment('');
                      setReviewSubmitted(false);
                      setTripJustCompleted(false);
                    }}
                  >
                    Book Another Ride
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* My Rides History */}
        <div>
          <Button
            variant="ghost"
            className="w-full text-gray-600"
            onClick={() => { setShowMyRides(!showMyRides); if (!showMyRides) fetchMyRides(); }}
          >
            {showMyRides ? <ChevronUp size={16} className="mr-1" /> : <ChevronDown size={16} className="mr-1" />}
            {showMyRides ? 'Hide' : 'Show'} My Rides ({myRides.length})
          </Button>

          {showMyRides && myRides.length > 0 && (
            <div className="space-y-2 mt-2">
              {myRides.slice(0, 10).map(ride => {
                const info = getStatusInfo(ride.status);
                const isCompleted = ride.status === 'completed';
                const isExpanded = expandedRideId === ride._id;
                return (
                  <div key={ride._id} className="bg-white rounded-lg border overflow-hidden">
                  {/* Ride row — clickable for completed rides */}
                  <div
                    className={`flex items-center gap-3 p-3 ${isCompleted ? 'cursor-pointer hover:bg-gray-50 transition-colors' : ''}`}
                    onClick={() => {
                      if (isCompleted) {
                        setExpandedRideId(isExpanded ? null : ride._id);
                        setHistoryReviewRating(0); setHistoryReviewComment(''); setHistoryReviewHover(0);
                      }
                    }}
                  >
                    <div className={`p-1.5 rounded-full ${info.color}`}>{info.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm">#{ride.rideNumber}</p>
                        <Badge className={`text-xs ${info.color}`}>{info.label}</Badge>
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {ride.pickupAddress} → {ride.dropoffAddress}
                        {ride.rated && <span className="ml-1 text-yellow-500">{'⭐'.repeat(ride.rating || 0)}</span>}
                      </p>
                    </div>
                    {ride.estimatedFare > 0 && (
                      <span className="font-bold text-sm">£{ride.estimatedFare.toFixed(2)}</span>
                    )}
                    {['requested', 'accepted', 'en_route', 'arrived_pickup', 'in_progress'].includes(ride.status) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveRide(ride);
                          setStep('tracking');
                          startTracking(ride._id);
                          if (ride.pickupLat && ride.dropoffLat) {
                            updateMapMarkers(
                              { lat: ride.pickupLat, lng: ride.pickupLng },
                              { lat: ride.dropoffLat, lng: ride.dropoffLng }
                            );
                          }
                        }}
                      >
                        Track
                      </Button>
                    )}
                    {isCompleted && (
                      <ChevronDown size={16} className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    )}
                  </div>

                  {/* Expanded section for completed rides — review + lost item */}
                  {isCompleted && isExpanded && (
                    <div className="px-3 pb-3 space-y-3 border-t bg-gray-50">
                      {/* Trip details */}
                      <div className="pt-3 text-xs text-gray-500 space-y-1">
                        {ride.driverName && <p>Driver: {ride.driverName}</p>}
                        {ride.vehicleType && <p className="capitalize">Vehicle: {ride.vehicleType} {ride.vehiclePlate ? `· ${ride.vehiclePlate}` : ''}</p>}
                        {ride.completedAt && <p>Completed: {new Date(ride.completedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>}
                      </div>

                      {/* Review section */}
                      {!ride.rated ? (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                          <h4 className="font-bold text-amber-900 text-center text-sm mb-2">⭐ Rate Your Driver</h4>
                          <div className="flex justify-center gap-2 mb-2">
                            {[1, 2, 3, 4, 5].map(star => (
                              <button key={star} onClick={() => setHistoryReviewRating(star)}
                                className="w-10 h-10 flex items-center justify-center text-2xl cursor-pointer focus:outline-none select-none">
                                {star <= historyReviewRating ? '⭐' : '☆'}
                              </button>
                            ))}
                          </div>
                          {historyReviewRating > 0 && (
                            <>
                              <p className="text-center text-xs text-amber-800 mb-2">
                                {historyReviewRating === 1 ? 'Poor' : historyReviewRating === 2 ? 'Fair' : historyReviewRating === 3 ? 'Good' : historyReviewRating === 4 ? 'Very Good' : 'Excellent!'}
                              </p>
                              <textarea value={historyReviewComment} onChange={e => setHistoryReviewComment(e.target.value)}
                                placeholder="Leave a comment (optional)…" rows={2}
                                className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none mb-2 bg-white" />
                              <Button onClick={() => submitHistoryReview(ride._id)} disabled={!historyReviewRating || historyReviewSubmitting}
                                className="w-full bg-amber-500 hover:bg-amber-600 text-white text-sm">
                                {historyReviewSubmitting ? 'Submitting…' : `Submit ${historyReviewRating}-Star Review`}
                              </Button>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center">
                          <p className="text-sm text-green-700">Rated {'⭐'.repeat(ride.rating || 0)}</p>
                        </div>
                      )}

                      {/* Lost item — Uber-style button opens modal */}
                      <Button size="sm" variant="ghost" className="w-full text-xs text-orange-600 hover:bg-orange-50"
                        onClick={() => openLostItemModal(ride)}>
                        🔍 Find Lost Item
                      </Button>
                    </div>
                  )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* === Uber-Style Cancellation Modal === */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={() => { setShowCancelModal(false); setCancelReason(''); }}>
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between rounded-t-2xl">
              <h3 className="text-lg font-bold text-gray-900">Cancel ride?</h3>
              <button onClick={() => { setShowCancelModal(false); setCancelReason(''); }} className="p-1 hover:bg-gray-100 rounded-full">
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            {/* Reason list */}
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-3">Please tell us why you're cancelling</p>
              <div className="space-y-2">
                {GUEST_CANCEL_REASONS.map(reason => (
                  <button
                    key={reason.id}
                    onClick={() => setCancelReason(reason.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                      cancelReason === reason.id
                        ? 'border-[#1F3A68] bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-xl flex-shrink-0">{reason.icon}</span>
                    <span className={`text-sm font-medium ${cancelReason === reason.id ? 'text-[#1F3A68]' : 'text-gray-700'}`}>
                      {reason.label}
                    </span>
                    {cancelReason === reason.id && (
                      <span className="ml-auto text-[#1F3A68]">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="sticky bottom-0 bg-white border-t p-4 space-y-2">
              <Button
                onClick={cancelRide}
                disabled={!cancelReason || cancelling}
                className="w-full bg-red-600 hover:bg-red-700 text-white h-12 text-base disabled:opacity-50"
              >
                {cancelling ? <Loader2 className="animate-spin mr-2" size={18} /> : <X size={18} className="mr-2" />}
                {cancelling ? 'Cancelling...' : 'Yes, Cancel Ride'}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setShowCancelModal(false); setCancelReason(''); }}
                className="w-full h-12 text-base border-gray-300"
              >
                Keep My Ride
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* === Location Permission Gate Modal === */}
      {showLocationGate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
              <MapPin size={32} className="text-[#1F3A68]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Location Required</h3>
            <p className="text-gray-600 text-sm mb-6">
              We need your location to verify your pickup, track your trip, and automatically complete your ride at the destination.
            </p>
            <div className="space-y-3">
              <Button
                className="w-full bg-[#1F3A68] hover:bg-[#1F3A68]/90 h-12 text-base"
                onClick={() => {
                  navigator.geolocation.getCurrentPosition(
                    () => handleLocationPermission('while_using'),
                    () => toast({ title: 'Location Denied', description: 'Please enable location access in your browser settings to book a ride.', variant: 'destructive' }),
                    { timeout: 10000 }
                  );
                }}
              >
                📍 Allow While Using App
              </Button>
              <Button
                variant="outline"
                className="w-full h-12 text-base border-gray-300"
                onClick={() => {
                  navigator.geolocation.getCurrentPosition(
                    () => handleLocationPermission('once'),
                    () => toast({ title: 'Location Denied', description: 'Please enable location access in your browser settings to book a ride.', variant: 'destructive' }),
                    { timeout: 10000 }
                  );
                }}
              >
                Allow Just This Once
              </Button>
              <button
                className="w-full text-sm text-gray-400 hover:text-gray-600 py-2"
                onClick={() => setShowLocationGate(false)}
              >
                Cancel
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-4">
              Your location is only used during your ride and never shared with third parties.
            </p>
          </div>
        </div>
      )}

      {/* === Post-Trip Review Popup === */}
      {showReviewPopup && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            {/* Close button */}
            <div className="flex justify-end mb-2">
              <button
                onClick={() => {
                  setShowReviewPopup(false);
                  setActiveRide(null);
                  setTrackingData(null);
                  setStep('input');
                  setPickupAddress('');
                  setDropoffAddress('');
                  setPickupCoords(null);
                  setDropoffCoords(null);
                  setFareEstimates([]);
                  setReviewRating(0);
                  setReviewHover(0);
                  setReviewComment('');
                  setReviewSubmitted(false);
                  setTripJustCompleted(false);
                  // Clear 'once' permission so it asks again next time
                  if (locationPermission === 'once') setLocationPermission(null);
                }}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X size={20} className="text-gray-400" />
              </button>
            </div>

            {/* Completion celebration */}
            <div className="text-center mb-4">
              <p className="text-4xl mb-2">🎉</p>
              <h3 className="text-xl font-bold text-gray-900">Trip Complete!</h3>
              <p className="text-sm text-gray-500 mt-1">
                You've arrived at your destination.
              </p>
              {(completedRideRef.current?.fareEstimate?.total || completedRideRef.current?.estimatedFare) && (
                <p className="text-lg font-bold text-[#1F3A68] mt-2">
                  £{(completedRideRef.current?.fareEstimate?.total || completedRideRef.current?.estimatedFare || 0).toFixed(2)}
                </p>
              )}
            </div>

            {!reviewSubmitted ? (
              <>
                <h4 className="font-bold text-amber-900 text-center mb-1">⭐ Rate Your Driver</h4>
                <p className="text-amber-700 text-xs text-center mb-3">
                  How was your ride with {completedRideRef.current?.driverName || 'your driver'}?
                </p>

                {/* Star rating */}
                <div className="flex justify-center gap-2 mb-3">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={() => setReviewRating(star)}
                      className="w-12 h-12 flex items-center justify-center text-3xl cursor-pointer focus:outline-none select-none"
                    >
                      {star <= reviewRating ? '⭐' : '☆'}
                    </button>
                  ))}
                </div>

                {reviewRating > 0 && (
                  <p className="text-center text-sm text-amber-800 mb-2">
                    {reviewRating === 1 ? 'Poor' : reviewRating === 2 ? 'Fair' : reviewRating === 3 ? 'Good' : reviewRating === 4 ? 'Very Good' : 'Excellent!'}
                  </p>
                )}

                {/* Optional comment */}
                <textarea
                  value={reviewComment}
                  onChange={e => setReviewComment(e.target.value)}
                  placeholder="Leave a comment (optional)…"
                  rows={2}
                  className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none mb-3 bg-white"
                />

                <Button
                  onClick={submitTaxiReview}
                  disabled={!reviewRating || reviewSubmitting}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50 mb-2"
                >
                  {reviewSubmitting ? 'Submitting…' : `Submit ${reviewRating ? reviewRating + '-Star' : ''} Review`}
                </Button>

                <button
                  onClick={() => {
                    setShowReviewPopup(false);
                    setActiveRide(null);
                    setTrackingData(null);
                    setStep('input');
                    setTripJustCompleted(false);
                    if (locationPermission === 'once') setLocationPermission(null);
                  }}
                  className="w-full text-sm text-gray-400 hover:text-gray-600 py-2"
                >
                  Skip Review
                </button>
              </>
            ) : (
              <>
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center mb-4">
                  <p className="text-2xl mb-1">🙏</p>
                  <p className="font-bold text-green-800">Thank you for your review!</p>
                  <p className="text-green-600 text-sm mt-1">
                    You rated your driver {reviewRating} star{reviewRating !== 1 ? 's' : ''}
                  </p>
                </div>
                <Button
                  className="w-full bg-[#1F3A68]"
                  onClick={() => {
                    setShowReviewPopup(false);
                    setActiveRide(null);
                    setTrackingData(null);
                    setStep('input');
                    setPickupAddress('');
                    setDropoffAddress('');
                    setPickupCoords(null);
                    setDropoffCoords(null);
                    setFareEstimates([]);
                    setReviewRating(0);
                    setReviewHover(0);
                    setReviewComment('');
                    setReviewSubmitted(false);
                    setTripJustCompleted(false);
                    if (locationPermission === 'once') setLocationPermission(null);
                  }}
                >
                  Book Another Ride
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* === Uber-Style Lost Item Modal === */}
      {showLostItemModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowLostItemModal(false)}>
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-2">
                {lostItemStep !== 'select' && lostItemStep !== 'success' && (
                  <button onClick={() => setLostItemStep(lostItemStep === 'describe' ? 'select' : 'describe')} className="p-1 hover:bg-gray-100 rounded-full">
                    <ChevronUp size={20} className="text-gray-500 rotate-[-90deg]" />
                  </button>
                )}
                <h3 className="text-lg font-bold text-gray-900">
                  {lostItemStep === 'success' ? 'Report Submitted' : 'Find Lost Item'}
                </h3>
              </div>
              <button onClick={() => setShowLostItemModal(false)} className="p-1 hover:bg-gray-100 rounded-full">
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            {/* Trip info bar */}
            {lostItemRide && lostItemStep !== 'success' && (
              <div className="px-4 py-2 bg-gray-50 border-b flex items-center gap-3 text-sm">
                <Car size={16} className="text-gray-400" />
                <div className="flex-1">
                  <span className="font-medium">{lostItemRide.driverName || 'Driver'}</span>
                  <span className="text-gray-400 mx-2">·</span>
                  <span className="text-gray-500">{lostItemRide.rideNumber}</span>
                </div>
                {lostItemRide.completedAt && (
                  <span className="text-xs text-gray-400">
                    {new Date(lostItemRide.completedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </span>
                )}
              </div>
            )}

            <div className="p-4">

              {/* Step 1: Select item category */}
              {lostItemStep === 'select' && (
                <div>
                  <p className="text-sm text-gray-600 mb-3">What did you leave behind?</p>
                  <div className="space-y-2">
                    {LOST_ITEM_CATEGORIES.map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => { setLostItemCategory(cat.id); setLostItemStep('describe'); }}
                        className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-gray-200 hover:border-[#1F3A68] hover:bg-blue-50 text-left transition-all"
                      >
                        <span className="text-2xl flex-shrink-0">{cat.icon}</span>
                        <span className="text-sm font-medium text-gray-700">{cat.label}</span>
                        <ChevronRight size={16} className="ml-auto text-gray-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Describe item */}
              {lostItemStep === 'describe' && (
                <div>
                  <div className="flex items-center gap-2 mb-3 p-2 bg-blue-50 rounded-lg">
                    <span className="text-xl">{LOST_ITEM_CATEGORIES.find(c => c.id === lostItemCategory)?.icon}</span>
                    <span className="text-sm font-medium text-[#1F3A68]">
                      {LOST_ITEM_CATEGORIES.find(c => c.id === lostItemCategory)?.label}
                    </span>
                  </div>

                  <label className="text-sm font-medium text-gray-700 block mb-2">Describe your item</label>
                  <textarea
                    value={lostItemDesc}
                    onChange={e => setLostItemDesc(e.target.value)}
                    placeholder="e.g. Black iPhone 14 in a blue case, left on back seat"
                    rows={3}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68] resize-none mb-4"
                    autoFocus
                  />

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-amber-700">
                        <p className="font-semibold mb-1">How it works:</p>
                        <ul className="list-disc pl-3 space-y-0.5">
                          <li>A temporary chat opens with your driver for 24 hours</li>
                          <li>Agree on a public meeting point for return</li>
                          <li>No personal info (phone, email) is shared</li>
                          <li>A £5 return fee will apply when the item is returned</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleReportLostItem}
                    disabled={!lostItemDesc.trim() || lostItemSubmitting}
                    className="w-full bg-[#1F3A68] hover:bg-[#1F3A68]/90 h-12 text-base disabled:opacity-50"
                  >
                    {lostItemSubmitting ? (
                      <><Loader2 size={18} className="mr-2 animate-spin" /> Contacting Driver...</>
                    ) : (
                      <>📨 Contact Driver About Lost Item</>
                    )}
                  </Button>
                </div>
              )}

              {/* Step 3: Success — show confirmation */}
              {lostItemStep === 'success' && (
                <div className="text-center py-4">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                    <span className="text-3xl">✅</span>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Report Submitted</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Your driver has been notified. A temporary chat has been opened so you can arrange the return of your item.
                  </p>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 text-left">
                    <p className="text-xs font-semibold text-blue-800 mb-1">Next steps:</p>
                    <ul className="text-xs text-blue-700 list-disc pl-4 space-y-1">
                      <li>Check your Messages for the driver conversation</li>
                      <li>Agree on a public place to meet</li>
                      <li>The chat expires in 24 hours</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <Button
                      className="w-full bg-[#1F3A68] h-11"
                      onClick={() => {
                        setShowLostItemModal(false);
                        // Navigate to messages tab with the lost item conversation
                        if (onNavigateToMessages) {
                          onNavigateToMessages(lostItemConvId);
                        } else {
                          toast({ title: '💬 Check your Messages', description: 'Open Messages to chat with your driver about the lost item.' });
                        }
                      }}
                    >
                      💬 Go to Messages
                    </Button>
                    <button
                      className="w-full text-sm text-gray-400 hover:text-gray-600 py-2"
                      onClick={() => setShowLostItemModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RideBooking;
